/**
 * Getting better by doing (RC2-02) — the practice tallies and their honest bands.
 *
 * The design the owner named is Ultima Online's: no class select, no skill tree,
 * no NPC handing you a perk. *You get good at what you practise, and the tool
 * only opens the door.* The engine already lives that in three places — a
 * forager's yield, a tracker's eye, a healer's hand each ride a hidden,
 * doing-fed, capped counter on the character's `Lore`. This module is that same
 * counter widened to the rest of the glen's first ring, plus the one thing those
 * three never needed: a way to *read* a tally out honestly, as a band a sheet
 * can show and a beat a player can feel.
 *
 * Three rules shape everything here, and each is a reconciliation with a
 * decision already taken:
 *
 * - **The tally is a count of reps, derived from events already emitted.** A
 *   fire lit (`camp.ts`), a carcass butchered, a meal cooked, a blow landed
 *   (`combat.ts`) — every rung of the ladder is an action the sim already logs.
 *   `familiarityForEvent` reads that stream; nothing new has to be emitted to
 *   grow a tally. This is `closeErrands`' discipline (RC2-01) applied to skill:
 *   a number cannot move without something having happened to move it.
 *
 * - **The five new counters are display-only (owner decision).** They feed the
 *   sheet's rating and the "getting better" line and *nothing mechanical* this
 *   milestone — no yield bonus, no extra die. Balancing waits on the
 *   skill-families milestone. `forage` / `tracking` / `medicine` keep the
 *   effects they already had; this module does not touch how those three are
 *   earned (their own systems still increment them), it only reads them into the
 *   same banded shape so the sheet shows one kind of progress, not four.
 *
 * - **The band is the honest surface, not the number.** Design pillar: the game
 *   shows no numbers it does not mean, and a rep-count is not a number a player
 *   should reason about. So the read-model hands out a *rating* — `untried` …
 *   `seasoned` — and crossing from one to the next is the once-only beat. How
 *   many reps a rating is worth is this file's business and no one else's.
 *
 * **The live producer is deferred, with RC2-01's.** `recordPractice` is the seam
 * that grows a tally off an event and returns the "getting better" beat to emit;
 * it wires into `advance`'s `emit` closure in the same coordinated pass that
 * arms `closeErrands` (RC2-01 deferred that by owner decision, and the two share
 * the seam). Until then this is a fully-tested engine the sheet (RC2-03) reads.
 */
import { createLore, MAX_FORAGE_FAMILIARITY, MAX_MEDICINE_FAMILIARITY, } from './item.js';
import { MAX_TRACKING_FAMILIARITY } from './sign.js';
/* -------------------------------------------------------------------------- *
 * The vocabulary
 * -------------------------------------------------------------------------- */
/**
 * The familiarities the glen's first ring introduces.
 *
 * Named to match their `Lore` field exactly, so the read-model can index one by
 * the other without a translation table — `lore[familiarity]` is the reps.
 * `forage`, `tracking` and `medicine` are the counters that already existed;
 * the other five are RC2-02's, and are display-only (see the header).
 *
 * Order is the glen's default ladder — firecraft first, the fight last — because
 * that is the order a sheet should list them in and the sequence RC2-01's arc
 * flexes the *first* rung of by background. Never reordered idly: the sheet
 * reads it.
 */
export const FAMILIARITIES = [
    'firecraft',
    'forage',
    'tracking',
    'medicine',
    'butchery',
    'cooking',
    'woodcraft',
    'fighting',
];
/**
 * Where practice stops mattering, per familiarity.
 *
 * The three older counters reuse the cap their own effect is tuned against, so a
 * `seasoned` rating lines up with the point their bonus tops out — the number a
 * player feels and the band they are shown agree. The five new ones share one
 * cap: with no mechanical effect to tune against, the only thing their cap sets
 * is how much doing a full band takes, and one shared value keeps that fair
 * across them. `MAX_TRACKING_FAMILIARITY` (40) is the smallest of the three, so
 * a new familiarity is neither the fastest nor the slowest thing to season.
 */
const FRESH_CAP = MAX_TRACKING_FAMILIARITY;
export const FAMILIARITY_CAP = {
    forage: MAX_FORAGE_FAMILIARITY,
    tracking: MAX_TRACKING_FAMILIARITY,
    medicine: MAX_MEDICINE_FAMILIARITY,
    firecraft: FRESH_CAP,
    butchery: FRESH_CAP,
    cooking: FRESH_CAP,
    woodcraft: FRESH_CAP,
    fighting: FRESH_CAP,
};
/** What the sheet calls each — plain English over the Welsh trade names. */
export const FAMILIARITY_LABEL = {
    firecraft: 'Firecraft',
    forage: 'Foraging',
    tracking: 'Tracking',
    medicine: 'Medicine',
    butchery: 'Butchery',
    cooking: 'Cooking',
    woodcraft: 'Woodcraft',
    fighting: 'Fighting',
};
/* -------------------------------------------------------------------------- *
 * The bands
 * -------------------------------------------------------------------------- */
/**
 * How practised something reads, coarsest to finest.
 *
 * Five bands, no numbers — a person genuinely knows their own hand as a band and
 * not as a count, and "you show no number you do not mean" makes the band the
 * only honest thing to surface. `untried` is its own band (zero reps) rather than
 * the bottom of `green`, because "never done it" and "done it once" are a
 * different sentence to the player and the clearest rung to cross first.
 */
export const RATINGS = ['untried', 'green', 'handy', 'practised', 'seasoned'];
/**
 * The rating a tally reads as, given its familiarity's cap.
 *
 * Banded on the *fraction* of the cap rather than raw reps, so the four
 * familiarities that share a cap and the three that do not all band on the same
 * curve: `seasoned` always means "near the top of what this counter tracks",
 * whatever that counter's ceiling is.
 */
export function ratingOf(familiarity, reps) {
    if (reps <= 0)
        return 'untried';
    const fraction = Math.min(1, reps / FAMILIARITY_CAP[familiarity]);
    if (fraction < 0.15)
        return 'green';
    if (fraction < 0.4)
        return 'handy';
    if (fraction < 0.75)
        return 'practised';
    return 'seasoned';
}
function ratingIndex(rating) {
    return RATINGS.indexOf(rating);
}
/* -------------------------------------------------------------------------- *
 * Reading a character's practice
 * -------------------------------------------------------------------------- */
/** The reps a character has in one familiarity. A missing counter reads as 0. */
export function repsOf(lore, familiarity) {
    if (!lore)
        return 0;
    const value = lore[familiarity];
    return typeof value === 'number' ? value : 0;
}
/**
 * Every familiarity, in ladder order, as banded ratings for the character sheet.
 *
 * The whole ladder, always — an `untried` familiarity is a real thing to show, a
 * door the player has not walked through yet, and hiding it would hide the map
 * of what the glen has to teach. RC2-03 decides what to do with `untried`; this
 * hands it the honest reading.
 */
export function ratingsOf(lore) {
    return FAMILIARITIES.map((familiarity) => {
        const reps = repsOf(lore, familiarity);
        return {
            familiarity,
            label: FAMILIARITY_LABEL[familiarity],
            reps,
            cap: FAMILIARITY_CAP[familiarity],
            rating: ratingOf(familiarity, reps),
        };
    });
}
/* -------------------------------------------------------------------------- *
 * The worded rung — what the sheet actually shows (RC2-03)
 * -------------------------------------------------------------------------- */
/**
 * The rank word a rung reads as — a short, diegetic phrase, never the band name
 * and never a number.
 *
 * The band (`green` … `seasoned`) is the machine truth; the *phrase* is what a
 * person would say of their own hand — "a coaxed flame", "a keen tracker". The
 * sheet (RC2-03) shows the phrase and hides the band, exactly as the design's
 * "no number you do not mean" pillar hides the reps behind it. One phrase per
 * (familiarity, band), because a keen tracker and a good cook are not the same
 * kind of good and the words should not pretend they are.
 *
 * `untried` shares one word across every familiarity: never having done a thing
 * is the same sentence whatever the thing is, and the clearest first rung to
 * cross.
 */
export const UNTRIED_PHRASE = 'untried';
export const RANK_PHRASE = {
    firecraft: {
        green: 'sparks, mostly smoke',
        handy: 'a coaxed flame',
        practised: 'a steady hearth',
        seasoned: 'fire at a touch',
    },
    forage: {
        green: 'eyes on the ground',
        handy: 'learning the beds',
        practised: 'a full satchel',
        seasoned: 'knows every bank',
    },
    tracking: {
        green: 'reads a clear print',
        handy: 'picks up the trail',
        practised: 'a keen tracker',
        seasoned: 'misses nothing',
    },
    medicine: {
        green: 'a clumsy binding',
        handy: 'a steady hand',
        practised: "a healer's touch",
        seasoned: 'trusted with a life',
    },
    butchery: {
        green: 'a ragged cut',
        handy: 'learning the joints',
        practised: 'a clean butcher',
        seasoned: 'nothing wasted',
    },
    cooking: {
        green: 'scorched and plain',
        handy: 'a warm meal',
        practised: 'a good cook',
        seasoned: 'a feast from little',
    },
    woodcraft: {
        green: 'chips and splinters',
        handy: 'a rough cut',
        practised: 'a true edge',
        seasoned: 'the wood obeys',
    },
    fighting: {
        green: 'a wild swing',
        handy: 'finding the range',
        practised: 'a hard hand',
        seasoned: 'a dangerous foe',
    },
};
/** The rank word for a rung — `untried` when no reps have been laid down yet. */
export function phraseFor(familiarity, rating) {
    return rating === 'untried' ? UNTRIED_PHRASE : RANK_PHRASE[familiarity][rating];
}
export const FAMILIARITY_DOORS = {
    woodcraft: { door: 'needs an axe', tool: 'felling' },
};
/**
 * The tool tag a familiarity's door asks for, or `undefined` for an open one.
 *
 * The read side of `FAMILIARITY_DOORS`: `snapshot.ts` calls it to decide whether
 * the viewer carries what a rung needs, so the "is the door open" question is
 * answered from the one table and not re-encoded on the wire.
 */
export function toolTagFor(familiarity) {
    return FAMILIARITY_DOORS[familiarity]?.tool;
}
/**
 * Every familiarity, in ladder order, as the character sheet shows it (RC2-03).
 *
 * The worded, wire-ready shape: `ratingsOf` read honestly and dressed for the
 * screen — the band turned into a phrase, the reps turned into a bar fraction (a
 * ratio the `Meter` fills, not a count anybody reads), and the door drawn where
 * a tool is still missing. `opened` answers "is this familiarity's door open" —
 * whether the tool is carried — and defaults to shut, which is the honest state
 * until RC2-04 puts an axe in the world.
 */
export function familiarityProgress(lore, opened = () => false) {
    return ratingsOf(lore).map((rung) => {
        const door = FAMILIARITY_DOORS[rung.familiarity];
        const locked = door !== undefined && !opened(rung.familiarity);
        return {
            familiarity: rung.familiarity,
            label: rung.label,
            phrase: locked ? door.door : phraseFor(rung.familiarity, rung.rating),
            band: rung.rating,
            fill: locked ? 0 : Math.min(1, rung.reps / rung.cap),
            locked,
        };
    });
}
/**
 * One rep of practice, capped — the write half of the counter.
 *
 * Mutates the lore in place, the way `forage.ts` and `care.ts` grow their own
 * counters: the tally lives in world state and a rule that had to thread a new
 * copy back would be a rule that forgot to. `crossed` is the whole point of the
 * return — it is non-`null` exactly on the rep that moves the rating up a band,
 * which is the once-only "getting better" beat and never fires twice for the
 * same rung.
 *
 * `by` is clamped non-negative: practice never un-teaches, and a caller that
 * passed a negative would be asking this to walk a rating *down*, which no rule
 * should be able to do through the practice door.
 */
export function practise(lore, familiarity, by = 1) {
    const before = repsOf(lore, familiarity);
    const beforeRating = ratingOf(familiarity, before);
    const reps = Math.min(FAMILIARITY_CAP[familiarity], before + Math.max(0, by));
    lore[familiarity] = reps;
    const rating = ratingOf(familiarity, reps);
    const crossed = ratingIndex(rating) > ratingIndex(beforeRating) ? rating : null;
    return { reps, crossed };
}
/* -------------------------------------------------------------------------- *
 * Reading the event stream
 * -------------------------------------------------------------------------- */
/**
 * The familiarity an event exercises, and for whom — or `null`.
 *
 * The mirror of `closeErrands`' `matchTrigger`, and it reads the same real
 * events (the ref shapes are cited). It maps **only the five new counters**: the
 * older three are grown at their own call sites (`forage.ts`, `speech.ts`,
 * `care.ts`) and reading them here as well would count every pick and every
 * treatment twice. `woodcraft` has no producer until RC2-04 arms the `chop`
 * verb, exactly as its errand trigger does not.
 *
 * Every familiarity here belongs to `actors[0]` — the fire's builder, the
 * butcher, the cook, the striker — so this reads one actor rather than walking
 * owners the way `closeErrands` must for a `kill` (whose owner is `actors[1]`).
 */
export function familiarityForEvent(event) {
    const actor = event.actors[0];
    if (!actor)
        return null;
    if (event.type === 'blow') {
        // combat.ts: a landed blow carries the weapon id in refs[0]; a miss carries
        // 'air'. Connecting — even with a shield in the way — is a rep of the fight.
        return event.refs[0] !== 'air' ? { actor, familiarity: 'fighting' } : null;
    }
    if (event.type === 'interacted') {
        const [what, phase] = event.refs;
        // camp.ts: interacted / ['build','lit', id] / actors[0]=builder
        if (what === 'build' && phase === 'lit')
            return { actor, familiarity: 'firecraft' };
        // camp.ts: interacted / ['butcher','yield', product, count] / actors[0]=doer
        if (what === 'butcher' && phase === 'yield')
            return { actor, familiarity: 'butchery' };
        // camp.ts: interacted / ['cook','done', cookedId, count] / actors[0]=cook
        if (what === 'cook' && phase === 'done')
            return { actor, familiarity: 'cooking' };
        // woodcraft.ts: interacted / ['chop','done', deadfallId] / actors[0]=chopper.
        // Every swing that lands is a rep, whether or not it cleared the deadfall —
        // and it cannot land without the axe (the `chop` fault gates on it), so the
        // woodcraft door gates this tally by construction (RC2-04 / RC2-02's
        // "acquisition + use").
        if (what === 'chop' && phase === 'done')
            return { actor, familiarity: 'woodcraft' };
    }
    return null;
}
/**
 * Grow the practice an event earned, and return the "getting better" beat.
 *
 * Shaped like `closeErrands`: it is called from the one emitter seam every event
 * passes through, so no rule has to remember to grow its own tally. It returns a
 * `practice` event **only on the rep that crosses a band** — an `actor`-audience
 * line the crosser sees once — so the common case adds nothing to the stream and
 * the rare case is the beat RC2-02 is for. The caller emits what comes back onto
 * the same stream (and must not re-feed it here: `familiarityForEvent` returns
 * `null` for a `practice` event, so a loop is impossible either way).
 */
export function recordPractice(world, event) {
    const hit = familiarityForEvent(event);
    if (!hit)
        return [];
    const lore = (world.lore[hit.actor] ??= createLore());
    const { crossed } = practise(lore, hit.familiarity);
    if (!crossed)
        return [];
    return [
        {
            type: 'practice',
            actors: [hit.actor],
            refs: ['better', hit.familiarity, crossed],
            audience: 'actor',
            to: hit.actor,
        },
    ];
}
//# sourceMappingURL=practice.js.map